/**
 * Stock Transfer Module
 * Identifies SKUs overstocked in one store and understocked in another (same city),
 * proposes inter-store transfers ranked by priority.
 *
 * Features:
 * - Filters: City, Brand, Category, Store, ABC Classification
 * - Transfer Suggestions table with priority-ranked target stores
 * - Editable transfer quantity
 * - Approve / Reject actions per row
 */
import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  Refresh,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import UniversalFilters from '../common/UniversalFilters';
import { TransferSuggestion } from '../../types';
import ExportMenu from '../Common/ExportMenu';
import { useFullscreen, FullscreenButton } from '../Common/FullscreenButton';

const StockTransfer: React.FC = () => {
  const { isFullscreen, toggle: toggleFullscreen, FullscreenWrap } = useFullscreen();
  const queryClient = useQueryClient();

  /* ── Top horizontal scrollbar, synced with the table ──
     The suggestions table is wider than the viewport on most screens, and the
     default horizontal scrollbar sits at the very bottom of the table — out of
     reach unless the user scrolls through every row first. We add a second,
     sticky scrollbar at the top of the table that slides left↔right and drives
     the table's scroll position bidirectionally. */
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const [scrollContentWidth, setScrollContentWidth] = useState(0);
  const syncingScrollRef = useRef(false);
  // Filter state
  const [filters, setFilters] = useState<{
    cities: string[];
    brands: string[];
    categories: string[];
    stores: string[];
    abcClassifications: string[];
  }>({
    cities: [],
    brands: [],
    categories: [],
    stores: [],
    abcClassifications: [],
  });

  // Stable per-row identity. Using array index breaks editing whenever the
  // suggestions list refetches, reorders, or has a row removed (after approval),
  // because state then sticks to the wrong row. Each suggestion has exactly one
  // source store per SKU so this composite is unique.
  const rowKey = (s: TransferSuggestion) => `${s.sku}-${s.source_store_id}`;

  // Editable transfer quantities keyed by stable row id
  const [transferQtys, setTransferQtys] = useState<Record<string, string>>({});

  // Expanded row id (to show target stores detail)
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Selected target store index per suggestion (keyed by stable row id)
  const [selectedTargets, setSelectedTargets] = useState<Record<string, number>>({}); // value = index into target_stores

  // Build API params. The suggestions endpoint uses a fixed 30-day ADU
  // window and a fixed target-coverage horizon, so no date range is
  // needed — the proposal always reflects "as of today" inventory state.
  const getParams = () => ({
    cities: filters.cities.length > 0 ? filters.cities.join(',') : undefined,
    brands: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
    categories: filters.categories.length > 0 ? filters.categories.join(',') : undefined,
    stores: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
    abc_classifications: filters.abcClassifications.length > 0 ? filters.abcClassifications.join(',') : undefined,
    limit: 200,
  });

  const { data: suggestions, isLoading, isError, error, refetch } = useQuery<TransferSuggestion[]>({
    queryKey: ['transferSuggestions', filters],
    queryFn: async () => {
      const response = await api.getTransferSuggestions(getParams());
      return response.data;
    },
  });

  // Approve = create a StockTransfer record
  const approveMutation = useMutation({
    mutationFn: (data: { product_id: string; from_store_id: string; to_store_id: string; recommended_qty: number }) =>
      api.createStockTransfer(data),
    onSuccess: () => {
      refetch();
      // Also refresh the Transfer History tab so the newly-validated
      // transfer shows up there immediately when the user navigates.
      queryClient.invalidateQueries({ queryKey: ['transferHistory'] });
      alert('Transfer approved and created successfully!');
    },
    onError: (err: any) => {
      alert(`Failed to create transfer: ${err.response?.data?.detail || err.message}`);
    },
  });

  const getTransferQty = useCallback(
    (suggestion: TransferSuggestion): number => {
      const raw = transferQtys[rowKey(suggestion)];
      if (raw === undefined) return suggestion.suggested_transfer_qty;
      const parsed = parseInt(raw, 10);
      return isNaN(parsed) ? 0 : parsed;
    },
    [transferQtys]
  );

  const getDisplayQty = useCallback(
    (suggestion: TransferSuggestion): string | number => {
      const raw = transferQtys[rowKey(suggestion)];
      return raw !== undefined ? raw : suggestion.suggested_transfer_qty;
    },
    [transferQtys]
  );

  const handleQtyChange = useCallback((key: string, value: string) => {
    // Allow empty string so the field can be fully cleared, plus any
    // non-negative integer. Reject anything else (letters, negatives, etc.).
    if (value === '' || /^\d+$/.test(value)) {
      setTransferQtys((prev) => ({ ...prev, [key]: value }));
    }
  }, []);

  const getSelectedTarget = (suggestion: TransferSuggestion) => {
    const targetIdx = selectedTargets[rowKey(suggestion)] ?? 0;
    return suggestion.target_stores[targetIdx] || suggestion.target_stores[0];
  };

  const handleApprove = (suggestion: TransferSuggestion) => {
    const target = getSelectedTarget(suggestion);
    if (!target) return;
    const qty = getTransferQty(suggestion);
    if (qty <= 0) {
      alert('Transfer quantity must be greater than 0.');
      return;
    }
    approveMutation.mutate({
      product_id: suggestion.product_id,
      from_store_id: suggestion.source_store_id,
      to_store_id: target.store_id,
      recommended_qty: qty,
    });
  };

  /* Keep the top scrollbar's inner width in sync with the table's scroll width.
     useLayoutEffect runs synchronously after DOM mutations so the bar has a
     correct width on the first paint. A ResizeObserver also catches any later
     layout changes (filters, resize, expand row, fullscreen toggle). */
  useLayoutEffect(() => {
    const tc = tableScrollRef.current;
    if (!tc) return;
    const update = () => {
      const tbl = tc.querySelector('table');
      // Prefer the table's intrinsic width — it's stable across re-layouts.
      // Fall back to scrollWidth if the table isn't found yet.
      const w = tbl ? tbl.scrollWidth : tc.scrollWidth;
      setScrollContentWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(tc);
    const tbl = tc.querySelector('table');
    if (tbl) ro.observe(tbl);
    window.addEventListener('resize', update);
    // Give the browser one more frame in case fonts/layout haven't settled.
    const raf = requestAnimationFrame(update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      cancelAnimationFrame(raf);
    };
  }, [suggestions, isFullscreen]);

  const handleTopScroll = useCallback(() => {
    if (syncingScrollRef.current) { syncingScrollRef.current = false; return; }
    if (tableScrollRef.current && topScrollRef.current) {
      syncingScrollRef.current = true;
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  }, []);

  const handleTableScroll = useCallback(() => {
    if (syncingScrollRef.current) { syncingScrollRef.current = false; return; }
    if (tableScrollRef.current && topScrollRef.current) {
      syncingScrollRef.current = true;
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  }, []);

  const getPriorityBadge = (target: { current_stock: number; stockout_days: number; dos: number }) => {
    if (target.current_stock === 0 && target.stockout_days > 7) {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 22,
          padding: '0 8px',
          borderRadius: 'var(--radius-full)',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 'var(--tracking-caps)',
          backgroundColor: 'var(--color-danger-bg)',
          color: 'var(--color-danger)',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
          P1
        </span>
      );
    }
    if (target.current_stock === 0) {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 22,
          padding: '0 8px',
          borderRadius: 'var(--radius-full)',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 'var(--tracking-caps)',
          backgroundColor: 'var(--color-danger-bg)',
          color: 'var(--color-danger)',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
          P1
        </span>
      );
    }
    if (target.dos < 14) {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 22,
          padding: '0 8px',
          borderRadius: 'var(--radius-full)',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 'var(--tracking-caps)',
          backgroundColor: 'var(--color-warning-bg)',
          color: 'var(--color-warning)',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
          P2
        </span>
      );
    }
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        padding: '0 8px',
        borderRadius: 'var(--radius-full)',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 'var(--tracking-caps)',
        backgroundColor: 'var(--color-info-bg)',
        color: 'var(--color-info)',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}>
        P3
      </span>
    );
  };

  return (
    <FullscreenWrap>
    <Box sx={{ maxWidth: 'var(--content-max-width)' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography
            sx={{
              fontSize: 'var(--text-xl)',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-heading)',
            }}
          >
            Stock Transfer Suggestions
          </Typography>
          <Typography
            sx={{
              fontSize: 'var(--text-base)',
              color: 'var(--color-text-muted)',
              mt: 0.5,
            }}
          >
            Identifies SKUs overstocked in one store and understocked in another within the same city,
            and proposes inter-store transfers ranked by priority.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => refetch()}
          disabled={isLoading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Filters: City, Brand, Category, Store, ABC Classification */}
      <Box sx={{ mb: 3 }}>
        <UniversalFilters
          cities={filters.cities}
          brands={filters.brands}
          categories={filters.categories}
          stores={filters.stores}
          abcClassifications={filters.abcClassifications}
          onCitiesChange={(cities) => setFilters({ ...filters, cities })}
          onBrandsChange={(brands) => setFilters({ ...filters, brands })}
          onCategoriesChange={(categories) => setFilters({ ...filters, categories })}
          onStoresChange={(stores) => setFilters({ ...filters, stores })}
          onAbcClassificationsChange={(abcClassifications) => setFilters({ ...filters, abcClassifications })}
          onClearAll={() => setFilters({ cities: [], brands: [], categories: [], stores: [], abcClassifications: [] })}
          showCountries={false}
          showCities={true}
          showRetailers={false}
          showBrands={true}
          showCategories={true}
          showStores={true}
          showAbcClassifications={true}
        />
      </Box>

      {/* KPI Summary */}
      {suggestions && suggestions.length > 0 && (() => {
        // Compute OOS avoidance value:
        // For each suggestion, the transfer moves stock to a deficit store.
        // Value = suggested_transfer_qty × retail_price (revenue potential of transferred units)
        // Extra sales from OOS recovery = target_avg_daily_sales × retail_price × min(stockout_days, 30)
        // for targets currently at zero stock
        let potentialRevenue = 0;
        let oosRecoveryValue = 0;
        suggestions.forEach((s) => {
          const price = s.retail_price || 0;
          potentialRevenue += s.suggested_transfer_qty * price;
          // For the top target store, compute OOS recovery if it's at zero stock
          const topTarget = s.target_stores[0];
          if (topTarget && topTarget.current_stock === 0 && topTarget.stockout_days > 0) {
            const recoveryDays = Math.min(topTarget.stockout_days, 30);
            oosRecoveryValue += (s.target_avg_daily_sales || 0) * price * recoveryDays;
          }
        });
        const totalValue = potentialRevenue + oosRecoveryValue;
        const fmtValue = (v: number) =>
          v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v / 1_000).toFixed(0)}K` : `${Math.round(v)}`;

        return (
        <Box
          sx={{
            mb: 3,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 2,
          }}
        >
          <Box
            sx={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '16px 20px',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <Typography
              sx={{
                fontSize: 'var(--text-xs)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-caps)',
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              Transfer Opportunities
            </Typography>
            <Typography
              sx={{
                fontSize: 'var(--text-2xl)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {suggestions.length}
            </Typography>
          </Box>
          <Box
            sx={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '16px 20px',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <Tooltip title={
              <Box sx={{ fontSize: 12 }}>
                <div>Transfer Revenue: {potentialRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                <div>OOS Recovery (30d): {oosRecoveryValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </Box>
            } arrow>
              <Box>
                <Typography
                  sx={{
                    fontSize: 'var(--text-xs)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--tracking-caps)',
                    color: 'var(--color-text-muted)',
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Potential Extra Sales
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 700,
                    color: '#2E7D32',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {fmtValue(totalValue)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-muted)',
                    mt: 0.25,
                  }}
                >
                  OOS avoidance + transfer value
                </Typography>
              </Box>
            </Tooltip>
          </Box>
          <Box
            sx={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '16px 20px',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <Typography
              sx={{
                fontSize: 'var(--text-xs)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-caps)',
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              Total Excess Qty
            </Typography>
            <Typography
              sx={{
                fontSize: 'var(--text-2xl)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {suggestions.reduce((s, r) => s + r.excess_qty, 0).toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '16px 20px',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <Typography
              sx={{
                fontSize: 'var(--text-xs)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-caps)',
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              Cities
            </Typography>
            <Typography
              sx={{
                fontSize: 'var(--text-2xl)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {[...new Set(suggestions.map((s) => s.city))].length}
            </Typography>
          </Box>
          <Box
            sx={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '16px 20px',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <Typography
              sx={{
                fontSize: 'var(--text-xs)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-caps)',
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              Products
            </Typography>
            <Typography
              sx={{
                fontSize: 'var(--text-2xl)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {[...new Set(suggestions.map((s) => s.sku))].length}
            </Typography>
          </Box>
        </Box>
        );
      })()}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading suggestions: {(error as any)?.message || 'Unknown error'}
        </Alert>
      )}

      {/* Export + Transfer Suggestions Table */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
        <ExportMenu
          data={(suggestions || []).map((s) => ({
            SKU: s.sku, Product: s.product_name, Brand: s.brand, Category: s.category,
            Status: s.status, ABC: s.abc_classification, City: s.city,
            'Source Store': s.source_store_name, 'Source DOS': s.source_dos,
            'Source Stock': s.source_current_stock, 'Excess Qty': s.excess_qty,
            'Suggested Transfer': s.suggested_transfer_qty,
          }))}
          filename={`StockTransfer_${new Date().toISOString().slice(0, 10)}`}
          title="Stock Transfer Suggestions"
        />
        <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />
      </Box>
      {/* Sticky top horizontal scrollbar — mirrors the table's scroll width so
          users can slide left↔right from the top of the tab without having to
          scroll all the way to the bottom to reach the native scrollbar. */}
      <Box
        ref={topScrollRef}
        onScroll={handleTopScroll}
        sx={{
          overflowX: 'scroll',
          overflowY: 'hidden',
          height: 18,
          mb: 1,
          position: 'sticky',
          top: 0,
          zIndex: 3,
          bgcolor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-sm)',
          // Force the horizontal scrollbar to always be visible across
          // platforms (macOS / iOS otherwise hide overlay scrollbars, which
          // defeats the purpose of this control).
          '&::-webkit-scrollbar': {
            height: 14,
            WebkitAppearance: 'none',
            display: 'block',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'var(--color-text-muted)',
            borderRadius: 7,
            border: '2px solid var(--color-bg-secondary)',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'var(--color-text-primary)',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'var(--color-bg-secondary)',
            borderRadius: 7,
          },
          scrollbarWidth: 'auto',
          scrollbarColor: 'var(--color-text-muted) var(--color-bg-secondary)',
        }}
      >
        {/* Spacer matches the table's scroll width so the bar scrolls in sync. */}
        <Box sx={{ width: Math.max(scrollContentWidth, 1), height: 1 }} />
      </Box>

      <TableContainer
        component={Paper}
        ref={tableScrollRef}
        onScroll={handleTableScroll}
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: isFullscreen ? 'calc(100vh - 200px)' : 'calc(100vh - 420px)',
          width: '100%',
        }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: 1500 }}>
          <TableHead>
            <TableRow>
              {[
                { label: '', align: 'left' as const },
                { label: 'SKU', align: 'left' as const },
                { label: 'Product', align: 'left' as const },
                { label: 'Brand', align: 'left' as const },
                { label: 'Category', align: 'left' as const },
                { label: 'Item Status', align: 'left' as const },
                { label: 'Stock Health', align: 'left' as const },
                { label: 'ABC', align: 'left' as const },
                { label: 'City', align: 'left' as const },
                { label: 'Transfer', align: 'left' as const },
                { label: 'Source DOS', align: 'right' as const },
                { label: 'Source Stock', align: 'right' as const },
                { label: 'Excess Qty', align: 'right' as const },
                { label: 'Priority', align: 'left' as const },
                { label: 'Transfer Qty', align: 'right' as const },
                { label: 'Actions', align: 'left' as const },
              ].map((col, i) => (
                <TableCell
                  key={i}
                  align={col.align}
                  sx={{
                    fontWeight: 700,
                    fontSize: '12px',
                    bgcolor: 'var(--color-surface)',
                    zIndex: 3,
                    whiteSpace: 'nowrap',
                    borderBottom: '2px solid var(--color-border)',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={16} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} sx={{ mr: 1 }} />
                  Loading transfer suggestions...
                </TableCell>
              </TableRow>
            ) : suggestions && suggestions.length > 0 ? (
              suggestions.map((suggestion) => {
                const key = rowKey(suggestion);
                const target = getSelectedTarget(suggestion);
                const isExpanded = expandedRow === key;

                return (
                  <React.Fragment key={key}>
                    <TableRow hover>
                      {/* Expand to show all target stores */}
                      <TableCell>
                        {suggestion.target_stores.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRow(isExpanded ? null : key)}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)' }}>
                          {suggestion.sku}
                        </Typography>
                      </TableCell>
                      <TableCell>{suggestion.product_name}</TableCell>
                      <TableCell>{suggestion.brand}</TableCell>
                      <TableCell>{suggestion.category}</TableCell>
                      <TableCell sx={{ py: '6px', px: '8px' }}>
                        {(suggestion as any).lifecycle_stage ? (
                          <Chip
                            label={(suggestion as any).lifecycle_stage}
                            size="small"
                            color={
                              (suggestion as any).lifecycle_stage === 'NEW' ? 'info' :
                              (suggestion as any).lifecycle_stage === 'GROWTH' ? 'success' :
                              (suggestion as any).lifecycle_stage === 'DECLINING' ? 'warning' :
                              ((suggestion as any).lifecycle_stage === 'PHASE_OUT' || (suggestion as any).lifecycle_stage === 'DISCONTINUED') ? 'error' :
                              'default'
                            }
                            variant="outlined"
                            sx={{ fontSize: '10px', height: 20 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${
                          suggestion.status === 'RED' ? 'status-badge--danger' :
                          suggestion.status === 'YELLOW' ? 'status-badge--warning' : 'status-badge--success'
                        }`}>
                          {suggestion.status || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={suggestion.abc_classification?.toUpperCase() || '-'}
                          size="small"
                          variant="filled"
                          sx={{
                            fontSize: '11px', height: 20, fontWeight: 700,
                            bgcolor: ({ A: '#e8f5e9', B: '#e3f2fd', C: '#fff3e0', D: '#fce4ec', E: '#f5f5f5' } as Record<string, string>)[suggestion.abc_classification?.toUpperCase() || ''] || '#f5f5f5',
                            color: ({ A: '#2e7d32', B: '#1565c0', C: '#e65100', D: '#c62828', E: '#616161' } as Record<string, string>)[suggestion.abc_classification?.toUpperCase() || ''] || '#616161',
                          }}
                        />
                      </TableCell>
                      <TableCell>{suggestion.city}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={`DOS: ${suggestion.source_dos} | Stock: ${suggestion.source_current_stock}`}>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 'var(--text-sm)',
                                color: 'var(--color-text-primary)',
                                fontWeight: 500,
                              }}
                            >
                              {suggestion.source_store_name}
                            </Typography>
                          </Tooltip>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--color-text-muted)',
                              mx: 0.5,
                            }}
                          >
                            {'\u2192'}
                          </Typography>
                          {target && (
                            <Tooltip title={`DOS: ${target.dos} | Stock: ${target.current_stock} | Deficit: ${target.deficit_qty}`}>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: 'var(--text-sm)',
                                  color: 'var(--color-text-primary)',
                                  fontWeight: 500,
                                }}
                              >
                                {target.store_name}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: 'var(--text-sm)', fontVariantNumeric: 'tabular-nums' }}>
                          {suggestion.source_dos}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: 'var(--text-sm)', fontVariantNumeric: 'tabular-nums' }}>
                          {suggestion.source_current_stock}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-danger)', fontVariantNumeric: 'tabular-nums' }}>
                          {suggestion.excess_qty}
                        </Typography>
                      </TableCell>
                      <TableCell>{target && getPriorityBadge(target)}</TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={getDisplayQty(suggestion)}
                          onChange={(e) => handleQtyChange(key, e.target.value)}
                          inputProps={{
                            min: 0,
                            max: suggestion.excess_qty,
                            style: { textAlign: 'right', width: 70 },
                          }}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Approve Transfer">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => handleApprove(suggestion)}
                              disabled={approveMutation.isPending}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Dismiss">
                            <IconButton color="default" size="small">
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded detail: all ranked target stores */}
                    {suggestion.target_stores.length > 1 && (
                      <TableRow>
                        <TableCell colSpan={15} sx={{ py: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 4, py: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: 'var(--text-sm)',
                                  fontWeight: 600,
                                  color: 'var(--color-text-secondary)',
                                  mb: 1,
                                }}
                              >
                                Ranked Target Stores (same city: {suggestion.city})
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Rank</TableCell>
                                    <TableCell>Store</TableCell>
                                    <TableCell align="right">Current Stock</TableCell>
                                    <TableCell align="right">DOS</TableCell>
                                    <TableCell align="right">Deficit Qty</TableCell>
                                    <TableCell align="right">Stockout Days</TableCell>
                                    <TableCell>Priority</TableCell>
                                    <TableCell>Select</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {suggestion.target_stores.map((t, tIdx) => (
                                    <TableRow
                                      key={t.store_id}
                                      selected={(selectedTargets[key] ?? 0) === tIdx}
                                    >
                                      <TableCell>#{t.rank}</TableCell>
                                      <TableCell>{t.store_name}</TableCell>
                                      <TableCell align="right">{t.current_stock}</TableCell>
                                      <TableCell align="right">{t.dos}</TableCell>
                                      <TableCell align="right">{t.deficit_qty}</TableCell>
                                      <TableCell align="right">
                                        {t.stockout_days > 0 ? `${t.stockout_days}d` : '-'}
                                      </TableCell>
                                      <TableCell>{getPriorityBadge(t)}</TableCell>
                                      <TableCell>
                                        <Button
                                          size="small"
                                          variant={
                                            (selectedTargets[key] ?? 0) === tIdx
                                              ? 'contained'
                                              : 'outlined'
                                          }
                                          onClick={() =>
                                            setSelectedTargets((prev) => ({
                                              ...prev,
                                              [key]: tIdx,
                                            }))
                                          }
                                        >
                                          {(selectedTargets[key] ?? 0) === tIdx ? 'Selected' : 'Select'}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={16} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                    No transfer suggestions found. Adjust filters or check that inventory data exists.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
    </FullscreenWrap>
  );
};

export default StockTransfer;
